import { useEffect, useState, useContext, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../../utils/axiosInstance";
import PaymentMethodForm from "./PaymentMethodForm";
import PaymentMethodList from "./PaymentMethodList";
import ConfirmModal from "../../../../../components/Modals/ConfirmModal";
import { AuthContext } from "../../../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../../../utils/constants/permissions";
import { btn } from "../../../../../components/Button/buttonStyles";

const PaymentMethodsPage = ({ vendorId }) => {
  const { user } = useContext(AuthContext);
  const readOnly = isReadOnly(user, MODULES.VENDORS);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [methodToDelete, setMethodToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action");
  const editId = searchParams.get("id");

  const fetchMethods = useCallback(async () => {
    try {
      const res = await api.get(`/vendor-payment-methods/${vendorId}`);
      setMethods(res.data.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch vendor payment methods");
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    if (vendorId) fetchMethods();
  }, [vendorId, fetchMethods]);

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) {
      next.set("action", nextAction);
    } else {
      next.delete("action");
    }
    if (id !== undefined && id !== null) {
      next.set("id", String(id));
    } else {
      next.delete("id");
    }
    setSearchParams(next, { replace: true });
  };

  const handleSave = async (formData) => {
    try {
      const payload = {
        ...formData,
        vendor_id: vendorId,
      };

      if (action === "edit" && editId) {
        await api.put(`/vendor-payment-methods/${editId}`, payload);
        await fetchMethods();
      } else {
        const res = await api.post(`/vendor-payment-methods`, payload);
        setMethods((prev) => [...prev, res.data.data]);
      }

      setAction(null);
      setDeleteError("");
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const handleDeleteClick = (id) => {
    const methodToRemove = methods.find((m) => m.id === id);

    if (methodToRemove?.is_default && methods.length > 1) {
      setDeleteError(
        "Cannot delete default payment method. Please set another payment method as default first."
      );
      return;
    }

    setMethodToDelete(id);
    setDeleteError("");
  };

  const handleDelete = async () => {
    if (!methodToDelete) return;
    try {
      await api.delete(`/vendor-payment-methods/${methodToDelete}`);
      setMethods((prev) => prev.filter((m) => m.id !== methodToDelete));
      setMethodToDelete(null);
      setDeleteError("");
    } catch (err) {
      console.error(err);
      const errorMsg =
        err.response?.data?.message || "Failed to delete vendor payment method";
      setDeleteError(errorMsg);
    }
  };

  if (loading)
    return (
      <div className="text-center mt-20 text-gray-500 text-sm">Loading...</div>
    );

  if (action === "add" || (action === "edit" && editId)) {
    let methodToEdit = null;
    if (action === "edit" && editId) {
      methodToEdit = methods.find((m) => String(m.id) === String(editId));
      if (!methodToEdit) {
        return (
          <div className="text-center mt-10 text-gray-500">
            Payment method not found.
          </div>
        );
      }
    }

    return (
      <PaymentMethodForm
        methodToEdit={methodToEdit}
        onSave={handleSave}
        onCancel={() => {
          setAction(null);
          setDeleteError("");
        }}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Vendor Payment Methods
        </h3>

        {!readOnly && (
          <button
            onClick={() => {
              setAction("add");
              setDeleteError("");
            }}
            className={btn("primary", "sm")}
          >
            Add Payment Method
          </button>
        )}
      </div>

      {error && <p className="text-danger-500 mb-4">{error}</p>}
      {deleteError && (
        <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded mb-4 flex items-start justify-between">
          <span>{deleteError}</span>
          <button
            onClick={() => setDeleteError("")}
            className="text-danger-900 hover:text-danger-700 ml-4"
          >
            ×
          </button>
        </div>
      )}

      <PaymentMethodList
        methods={methods}
        readOnly={readOnly}
        onEdit={(method) => {
          setAction("edit", method.id);
          setDeleteError("");
        }}
        onDelete={handleDeleteClick}
      />

      {methodToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message="Are you sure you want to delete this vendor payment method?"
          onCancel={() => {
            setMethodToDelete(null);
            setDeleteError("");
          }}
          onConfirm={handleDelete}
          confirmText="Delete"
        />
      )}
    </div>
  );
};

export default PaymentMethodsPage;
